const DEFAULT_CHECKOUT_URL = "https://pay.kuti.pe";
const MESSAGE_SOURCE = "kuti-checkout";

export type KutiCheckoutStatus = "SUCCEEDED" | "FAILED" | "CANCELLED" | "EXPIRED";
export type KutiCheckoutFailureStatus = "FAILED" | "CANCELLED";

export interface KutiCheckoutError {
  code: string;
  message: string;
}

export type KutiCheckoutFont = "inter" | "poppins" | "montserrat" | "nunito" | "lora";
export type KutiCheckoutButtonShape = "square" | "rounded" | "pill";

/**
 * Personalización visual del checkout embebido. Cada campo es opcional y, si lo mandas, reemplaza
 * solo ese dato de la marca configurada en el panel (Ajustes → Marca); lo que no mandes se toma de
 * ahí. Sin marca en el panel, se usa la de KUTI.
 */
export interface KutiCheckoutAppearance {
  /** Color de acento (botón de pago, links) — hex, ej. "#0f766e". Reemplaza el verde por defecto de KUTI. */
  primaryColor?: string;
  /** URL de una imagen de logo. Reemplaza el nombre de texto del merchant en el header del checkout. */
  logo?: string;
  /** Tipografía del checkout. */
  font?: KutiCheckoutFont;
  /** Forma de botones, campos y pestañas: cuadrados, redondeados (KUTI) o tipo píldora. */
  buttonShape?: KutiCheckoutButtonShape;
}

export interface KutiCheckoutOpenOptions {
  /**
   * checkout_url devuelto por POST /checkout-sessions (creado en tu backend con la secret key) —
   * ya trae el client_secret embebido. Es lo único que necesitas pasar en el caso normal.
   */
  checkoutUrl?: string;
  /** Alternativa a checkoutUrl si prefieres armar la URL tú mismo. */
  clientSecret?: string;
  /**
   * Id de un elemento de tu página donde insertar el checkout directamente (sin modal/overlay) —
   * útil para embeberlo dentro de un paso de tu propio flujo de compra. Si lo omites, se abre
   * como modal (comportamiento por defecto).
   */
  containerId?: string;
  /** Personalización visual del checkout — color, logo, tipografía y forma de botones. */
  appearance?: KutiCheckoutAppearance;
  onSuccess?: (event: { paymentIntentId: string }) => void;
  /** El cobro terminó en FAILED o CANCELLED — no tiene sentido reabrir el mismo checkout. */
  onFailure?: (event: { paymentIntentId: string; status: KutiCheckoutFailureStatus }) => void;
  /** El cobro venció sin pagarse — normalmente aquí conviene crear una sesión nueva y reabrir. */
  onExpired?: (event: { paymentIntentId: string }) => void;
  /** El usuario cerró el modal (X, click fuera o Esc) sin resolver el pago. No aplica en modo inline. */
  onClose?: () => void;
  /** Falló la integración misma (ej. el iframe no cargó) — no es un estado del cobro. */
  onError?: (error: KutiCheckoutError) => void;
}

interface StatusMessage {
  source: typeof MESSAGE_SOURCE;
  type: "kuti:status";
  payment_intent_id: string;
  status: KutiCheckoutStatus;
}

function isStatusMessage(data: unknown): data is StatusMessage {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.source === MESSAGE_SOURCE &&
    d.type === "kuti:status" &&
    typeof d.payment_intent_id === "string" &&
    typeof d.status === "string"
  );
}

interface ResizeMessage {
  source: typeof MESSAGE_SOURCE;
  type: "kuti:resize";
  height: number;
}

function isResizeMessage(data: unknown): data is ResizeMessage {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.source === MESSAGE_SOURCE &&
    d.type === "kuti:resize" &&
    typeof d.height === "number" &&
    Number.isFinite(d.height)
  );
}

function buildCheckoutSrc(options: KutiCheckoutOpenOptions): string {
  const base = options.checkoutUrl
    ?? `${DEFAULT_CHECKOUT_URL}/embed?cs=${encodeURIComponent(options.clientSecret as string)}`;

  const { primaryColor, logo, font, buttonShape } = options.appearance ?? {};
  if (!primaryColor && !logo && !font && !buttonShape) return base;

  const url = new URL(base, typeof window !== "undefined" ? window.location.href : undefined);
  if (primaryColor) url.searchParams.set("primary_color", primaryColor);
  if (logo) url.searchParams.set("logo", logo);
  if (font) url.searchParams.set("font", font);
  if (buttonShape) url.searchParams.set("button_shape", buttonShape);
  return url.toString();
}

type Mode = "modal" | "inline";

let mode: Mode | null = null;
let overlay: HTMLDivElement | null = null;
let inlineContainer: HTMLElement | null = null;
let iframe: HTMLIFrameElement | null = null;
let messageHandler: ((event: MessageEvent) => void) | null = null;
let keydownHandler: ((event: KeyboardEvent) => void) | null = null;
let currentOptions: KutiCheckoutOpenOptions | null = null;
let loader: HTMLDivElement | null = null;
let revealTimer: ReturnType<typeof setTimeout> | null = null;

/** Alto mientras carga (parecido al checkout real). El iframe se muestra al primer `kuti:resize`. */
const LOADING_HEIGHT = "420px";

/** Cubre el iframe mientras carga, para que no aparezca grande y luego se achique. */
function createLoader(): HTMLDivElement {
  const el = document.createElement("div");
  el.textContent = "Cargando…";
  el.setAttribute("aria-live", "polite");
  Object.assign(el.style, {
    position: "absolute",
    inset: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    font: "14px system-ui, -apple-system, 'Segoe UI', sans-serif",
    color: "#64748b",
    pointerEvents: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  return el;
}

function reveal(): void {
  if (revealTimer) {
    clearTimeout(revealTimer);
    revealTimer = null;
  }
  if (iframe) iframe.style.opacity = "1";
  loader?.remove();
  loader = null;
}

function open(options: KutiCheckoutOpenOptions): void {
  if (!options.checkoutUrl && !options.clientSecret) {
    options.onError?.({
      code: "MISSING_CHECKOUT_URL",
      message: "checkoutUrl or clientSecret is required",
    });
    return;
  }

  let container: HTMLElement | null = null;
  if (options.containerId) {
    container = document.getElementById(options.containerId);
    if (!container) {
      options.onError?.({
        code: "CONTAINER_NOT_FOUND",
        message: `No element with id "${options.containerId}" was found.`,
      });
      return;
    }
  }

  close();
  currentOptions = options;

  const src = buildCheckoutSrc(options);

  iframe = document.createElement("iframe");
  iframe.src = src;
  iframe.title = "Pagar con KUTI";
  iframe.addEventListener("error", () => {
    currentOptions?.onError?.({
      code: "CHECKOUT_LOAD_FAILED",
      message: "No se pudo cargar el checkout de KUTI.",
    });
  });

  loader = createLoader();

  if (container) {
    mode = "inline";
    inlineContainer = container;
    Object.assign(iframe.style, {
      width: "100%",
      height: LOADING_HEIGHT,
      border: "0",
      display: "block",
      background: "#fff",
      overflow: "hidden",
      opacity: "0",
      transition: "opacity .2s ease, height .2s ease",
    } satisfies Partial<CSSStyleDeclaration>);
    container.style.overflow = "hidden";
    // Envoltorio propio (position: relative) para el "Cargando…" sin tocar el estilo del host.
    const wrapper = document.createElement("div");
    wrapper.style.position = "relative";
    wrapper.appendChild(iframe);
    wrapper.appendChild(loader);
    container.appendChild(wrapper);
  } else {
    mode = "modal";
    overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "2147483000",
      background: "rgba(15, 23, 42, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
    } satisfies Partial<CSSStyleDeclaration>);
    overlay.setAttribute("data-kuti-checkout-overlay", "");

    const panel = document.createElement("div");
    Object.assign(panel.style, {
      position: "relative",
      width: "100%",
      maxWidth: "440px",
      maxHeight: "90vh",
      background: "transparent",
      borderRadius: "16px",
      overflowX: "hidden",
      overflowY: "auto",
    } satisfies Partial<CSSStyleDeclaration>);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", "Cerrar");
    closeButton.textContent = "×";
    Object.assign(closeButton.style, {
      position: "absolute",
      top: "8px",
      right: "8px",
      width: "32px",
      height: "32px",
      borderRadius: "9999px",
      border: "none",
      background: "rgba(255,255,255,0.9)",
      color: "#0f172a",
      fontSize: "20px",
      lineHeight: "1",
      cursor: "pointer",
      zIndex: "1",
    } satisfies Partial<CSSStyleDeclaration>);
    closeButton.addEventListener("click", () => close(true));

    Object.assign(iframe.style, {
      width: "100%",
      height: LOADING_HEIGHT,
      border: "0",
      display: "block",
      background: "#fff",
      borderRadius: "16px",
      opacity: "0",
      transition: "opacity .2s ease, height .2s ease",
    } satisfies Partial<CSSStyleDeclaration>);
    // Mientras el iframe está oculto, el panel pone el fondo blanco detrás del "Cargando…".
    panel.style.background = "#fff";

    panel.appendChild(closeButton);
    panel.appendChild(iframe);
    panel.appendChild(loader);
    overlay.appendChild(panel);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close(true);
    });

    keydownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    document.addEventListener("keydown", keydownHandler);

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";
  }

  messageHandler = (event: MessageEvent) => {
    if (!iframe || event.source !== iframe.contentWindow) return;
    if (isResizeMessage(event.data)) {
      const h = Math.max(320, Math.ceil(event.data.height));
      // Inline (Woo / hosts): el iframe crece con el contenido y hace scroll la página.
      // Modal: iframe a altura real; si pasa de 90vh, scrollea el panel.
      iframe.style.height = `${h}px`;
      iframe.style.minHeight = "0";
      reveal();
      return;
    }
    if (!isStatusMessage(event.data)) return;
    handleStatus(event.data);
  };
  window.addEventListener("message", messageHandler);
  // Respaldo: si el checkout no avisa su alto (versión vieja, error de red), se muestra igual.
  revealTimer = setTimeout(reveal, 4000);
}

function handleStatus(message: StatusMessage): void {
  const options = currentOptions;
  if (!options) return;

  switch (message.status) {
    case "SUCCEEDED":
      options.onSuccess?.({ paymentIntentId: message.payment_intent_id });
      break;
    case "EXPIRED":
      options.onExpired?.({ paymentIntentId: message.payment_intent_id });
      break;
    case "FAILED":
    case "CANCELLED":
      options.onFailure?.({
        paymentIntentId: message.payment_intent_id,
        status: message.status,
      });
      break;
  }
}

function close(userInitiated = false): void {
  if (revealTimer) {
    clearTimeout(revealTimer);
    revealTimer = null;
  }
  loader = null;
  if (messageHandler) {
    window.removeEventListener("message", messageHandler);
    messageHandler = null;
  }
  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler);
    keydownHandler = null;
  }
  if (overlay) {
    overlay.remove();
    overlay = null;
    document.body.style.overflow = "";
  }
  if (inlineContainer) {
    inlineContainer.replaceChildren();
    inlineContainer = null;
  }
  iframe = null;

  const wasModal = mode === "modal";
  mode = null;
  const options = currentOptions;
  currentOptions = null;
  // onClose es semánticamente "el usuario cerró el modal" — en modo inline no hay nada que
  // "cerrar" desde el punto de vista del usuario, así que no se dispara ahí.
  if (userInitiated && wasModal) {
    options?.onClose?.();
  }
}

/**
 * KutiCheckout.close(): cierra el modal o limpia el contenedor inline programáticamente (ej. tras
 * tu propio timeout). No dispara onClose — ese callback es solo para el cierre iniciado por el usuario.
 */
const KutiCheckout = { open, close: () => close(false) };
export default KutiCheckout;
